import { TrackManagerCarReviewsHistoryReview } from "@/types/track_manager_car_reviews";
import { Badge, Flex, Text } from "@radix-ui/themes";
import { router } from "@inertiajs/react";
import {
  ColumnDef,
  ColumnFiltersState,
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  Row,
  useReactTable,
} from "@tanstack/react-table";
import React, { useMemo } from "react";
import { dateRangeFilter } from "@/shared/tables/utility";
import { badgeColors } from "@/shared/constants/constants";
import BasicTable from "../BasicTable";

type Review = TrackManagerCarReviewsHistoryReview;

const statusOptions = [
  { value: "all", title: "All" },
  { value: "approved", title: "Approved" },
  { value: "rejected", title: "Rejected" },
];

export default function CarReviewsTable({ reviews }: { reviews: Review[] }) {
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const columnHelper = createColumnHelper<Review>();

  const columns = useMemo<ColumnDef<Review, any>[]>(
    () => [
      columnHelper.accessor((row) => `${row.car_heading} ${row.driver_name}`, {
        id: "car",
        header: "Car",
        cell: (props) => (
          <Flex direction="column">
            <Text>{props.row.original.car_heading}</Text>
            <Text color="gray" size="1">{props.row.original.driver_name}</Text>
          </Flex>
        ),
        meta: { filterVariant: "text" },
      }),
      columnHelper.accessor("decision", {
        header: "Decision",
        cell: (props) => (
          <Flex direction="column" gap="1" align="start">
            <Badge variant="soft" color={badgeColors[props.getValue()]}>
              <Text as="p" style={{ textTransform: "capitalize" }}>{props.getValue()}</Text>
            </Badge>
            {props.row.original.reason_label && (
              <Text color="gray" size="1">{props.row.original.reason_label}</Text>
            )}
            {props.row.original.settled_reports && (
              <Badge variant="outline" color="red" size="1">Reported</Badge>
            )}
          </Flex>
        ),
        meta: { filterVariant: "select", selectOptions: statusOptions },
      }),
      columnHelper.accessor("current_status", {
        header: "Now",
        cell: (props) => (
          <Badge variant="soft" color={badgeColors[props.getValue()]}>
            <Text as="p" style={{ textTransform: "capitalize" }}>{props.getValue()}</Text>
          </Badge>
        ),
        meta: {
          filterVariant: "select",
          selectOptions: [...statusOptions, { value: "pending", title: "Pending" }],
        },
      }),
      columnHelper.accessor("reviewer_email", {
        header: "Reviewer",
        meta: { filterVariant: "text" },
      }),
      columnHelper.accessor("decided_at_raw", {
        header: "Decided",
        cell: (props) => props.row.original.decided_at,
        filterFn: dateRangeFilter,
        meta: { filterVariant: "dateRange" },
      }),
    ],
    [],
  );

  const table = useReactTable({
    data: reviews,
    columns,
    state: { columnFilters },
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <BasicTable
      table={table}
      handleRowOnClick={(row: Row<Review>) => router.visit(`/track_manager/car_reviews/${row.original.car_id}`)}
    />
  );
}
